import { z } from 'zod'
import { BLOOD_GROUPS, GUARDIAN_RELATIONS } from './types'

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Use at most ${max} characters.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()

const optionalEmail = z
  .union([z.literal(''), z.email('Enter a valid email address.').max(180)])
  .transform((v) => (v === '' ? null : v))
  .nullable()

export const guardianSchema = z.object({
  id: z.number().nullable(),
  relation: z.enum(GUARDIAN_RELATIONS, { error: 'Choose a relation.' }),
  name: z.string().trim().min(1, 'Enter the guardian’s name.').max(150),
  phone: optionalText(30),
  email: optionalEmail,
  occupation: optionalText(100),
  isPrimary: z.boolean(),
})

export const studentProfileSchema = z.object({
  admissionNumber: z
    .string()
    .trim()
    .min(1, 'Enter the admission number.')
    .max(30)
    .regex(/^[A-Za-z0-9/\-_.]+$/, 'Use letters, digits and - / _ . only.'),
  firstName: z.string().trim().min(1, 'Enter the first name.').max(80),
  lastName: z.string().trim().min(1, 'Enter the last name.').max(80),
  gender: z.enum(['male', 'female', 'other'], { error: 'Choose a gender.' }),
  dateOfBirth: z
    .string()
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .refine((v) => v === null || new Date(v) < new Date(), 'Date of birth must be in the past.'),
  bloodGroup: z
    .union([z.literal(''), z.enum(BLOOD_GROUPS)])
    .transform((v) => (v === '' ? null : v))
    .nullable(),
  religion: optionalText(50),
  nationality: optionalText(50),
  email: optionalEmail,
  phone: optionalText(30),
  address: optionalText(2000),
  medicalNotes: optionalText(2000),
  photoFileId: z.number().nullable(),
  guardians: z.array(guardianSchema).max(4, 'At most 4 guardians.'),
})

export const admitStudentSchema = studentProfileSchema.extend({
  classId: z.string().min(1, 'Choose a class.'),
  sectionId: z.string().min(1, 'Choose a section.'),
  rollNumber: optionalText(20),
  admissionDate: z.string().min(1, 'Enter the admission date.'),
})

export type StudentProfileInput = z.input<typeof studentProfileSchema>
export type StudentProfileOutput = z.output<typeof studentProfileSchema>
export type AdmitStudentInput = z.input<typeof admitStudentSchema>
export type AdmitStudentOutput = z.output<typeof admitStudentSchema>

export const promoteSchema = z.object({
  academicYearId: z.string().min(1, 'Choose the academic year.'),
  classId: z.string().min(1, 'Choose a class.'),
  sectionId: z.string().min(1, 'Choose a section.'),
  rollNumber: optionalText(20),
})
export type PromoteInput = z.input<typeof promoteSchema>

export const emptyGuardian = {
  id: null,
  relation: 'father' as const,
  name: '',
  phone: '',
  email: '',
  occupation: '',
  isPrimary: false,
}
